import { useEffect, useState } from "react";
import { fetchGuide } from "../utils/guideAuth";
import { NdaPrompt } from "../components/UWGuideContent";

// 🔑 THE MODEL IS SERVED, NOT BUNDLED (Jesse, 2026-10-21). GET /api/forecast/model (backend/constants/forecastModel.js)
// answers staff sessions and approved DoorlyMarket investors with a signed NDA only; this page renders what arrives and
// carries none of the assumptions, rates, loan ramp or expenses. No password.

const C = {
  blue: "#3a6ad9",
  dark: "#111827",
  gray: "#6b7280",
  lgray: "#f3f4f6",
  red:  "#ff082f",
  amber:"#ffdd86",
};

const fmtM = (n) => {
  if (n === null || n === undefined) return "—";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return sign + "$" + (abs/1e6).toFixed(1) + "M";
  if (abs >= 1e3) return sign + "$" + Math.round(abs/1e3) + "k";
  return sign + "$" + abs.toLocaleString();
};
const sum = (arr) => (arr||[]).reduce((a,b)=>a+(b||0),0);

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background:"#fff", border:"1px solid #e5e7eb", borderRadius:10,
      padding:"16px 20px", borderTop:`3px solid ${accent||C.blue}`, minWidth:130, flex:1,
    }}>
      <div style={{ fontSize:10, color:C.gray, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:5 }}>{label}</div>
      <div style={{ fontSize:24, fontWeight:700, color:C.dark, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:C.gray, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function YearSummary({ d, avgLoanAmount }) {
  const totRev   = sum(d.revenue);
  const totCogs  = sum(d.totalCogs);
  const totGross = sum(d.grossProfit);
  const totMkt   = sum(d.marketing);
  const totExp   = sum(d.expenses);
  const totNet   = sum(d.net);
  const margin      = totRev > 0 ? (totNet/totRev*100).toFixed(1)   : "—";
  const grossMargin = totRev > 0 ? (totGross/totRev*100).toFixed(1) : "—";
  const cacPct      = totRev > 0 ? (totMkt/totRev*100).toFixed(1)   : "—";
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginBottom:20 }}>
      <StatCard label="Loans Originated"       value={sum(d.loans).toLocaleString()} />
      <StatCard label="Origination Volume" value={"$"+Math.round(sum(d.loans)*avgLoanAmount/1e6)+"M"} />
      <StatCard label="Total Revenue"      value={"$"+Math.round(totRev/1e6)+"M"}  accent={C.blue} />
      <StatCard label="Total COGS"         value={"$"+Math.round(totCogs/1e6)+"M"} accent={C.amber} />
      <StatCard label="Gross Profit"       value={"$"+Math.round(totGross/1e6)+"M"} accent={C.blue}
        sub={`${grossMargin}% gross margin`} />
      <StatCard label="Marketing / CAC"    value={"$"+Math.round(totMkt/1e6)+"M"}  accent={C.amber}
        sub={`${cacPct}% of revenue`} />
      <StatCard label="Other Opex"         value={"$"+Math.round(totExp/1e6)+"M"}  accent={C.amber} />
      <StatCard label="Net Income (After Tax)" value={"$"+Math.round(totNet/1e6)+"M"}
        accent={totNet>=0?"#16a34a":C.red} sub={`${margin}% net margin`} />
    </div>
  );
}

// ── Excel export ───────────────────────────────────────────────────────────
// Emits a CSV (opens directly in Excel — same convention as the DoorlyDesk
// report exports in utils/exportUtils.js). All three years, every line item,
// each month + a Total column, using RAW rounded-dollar values — NOT the
// abbreviated "$1.2M" on-screen format — so the numbers are usable in a sheet.
function exportForecastToExcel(model) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const today = new Date().toISOString().slice(0, 10);
  const rows = [
    [model.title],
    [model.confidentiality],
    [`Generated ${today}`],
    [],
  ];
  for (const year of Object.keys(model.years)) {
    const d = model.years[year];
    rows.push([`${year}`]);
    rows.push(["Line Item", ...d.months, "Total"]);
    for (const [secId, secLabel] of model.sections) {
      rows.push([secLabel]);
      for (const row of model.rows[secId]) {
        const vals = (d[row.key] || []).map((v) => Math.round(v || 0));
        const total = vals.reduce((a, b) => a + b, 0);
        rows.push([row.label.trim(), ...vals, total]);
      }
    }
    rows.push([]);
  }
  const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
  // Prepend a UTF-8 BOM so Excel renders the em-dash / middot correctly.
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Doorly_Financial_Forecast_2026-2028_${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DataTable({ d, defs }) {
  const hdr = {
    position:"sticky", top:0, background:"#111827", color:"#fff",
    fontSize:11, fontWeight:600, padding:"8px 8px", textAlign:"right", whiteSpace:"nowrap",
  };
  return (
    <div style={{ overflowX:"auto", borderRadius:8, border:"1px solid #e5e7eb" }}>
      <table style={{ borderCollapse:"collapse", width:"100%", minWidth:700 }}>
        <thead>
          <tr>
            <th style={{...hdr, textAlign:"left", position:"sticky", left:0, background:"#111827", minWidth:230}}>Line Item</th>
            {d.months.map(m=><th key={m} style={hdr}>{m}</th>)}
            <th style={{...hdr, background:"#1e3a8a"}}>Total</th>
          </tr>
        </thead>
        <tbody>
          {defs.map(row=>{
            const vals = d[row.key] || [];
            const total = sum(vals);
            return (
              <tr key={row.key} style={{ background:row.bold?"#f8fafc":"#fff" }}>
                <td style={{
                  fontSize:11, padding:"6px 8px", textAlign:"left", whiteSpace:"nowrap",
                  fontWeight:row.bold?700:400, color:C.dark,
                  borderBottom:"1px solid #f3f4f6",
                  position:"sticky", left:0, background:row.bold?"#f8fafc":"#fff",
                }}>{row.label}</td>
                {vals.map((v,i)=>(
                  <td key={i} style={{
                    fontSize:11, padding:"6px 8px", textAlign:"right",
                    fontWeight:row.bold?700:400,
                    color:typeof v==="number"&&v<0?C.red:C.dark,
                    borderBottom:"1px solid #f3f4f6",
                  }}>
                    {row.key==="loans" ? v : fmtM(v)}
                  </td>
                ))}
                <td style={{
                  fontSize:11, padding:"6px 8px", textAlign:"right", fontWeight:700,
                  background:"#eef2fd", color:total<0?C.red:"#1e3a8a",
                  borderBottom:"1px solid #f3f4f6",
                }}>
                  {row.key==="loans" ? total.toLocaleString() : fmtM(total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CumulativeBar({ model }) {
  const yrs = Object.keys(model.years);
  const ann = yrs.map(y=>sum(model.years[y].revenue));
  const cum = [ann[0], ann[0]+ann[1], ann[0]+ann[1]+ann[2]];
  const max = cum[2];
  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.dark, marginBottom:12 }}>Cumulative Revenue</div>
      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {yrs.map((y,i)=>(
          <div key={y} style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:38, fontSize:12, fontWeight:600, color:C.gray }}>{y}</div>
            <div style={{ flex:1, background:"#f3f4f6", borderRadius:4, height:26, overflow:"hidden" }}>
              <div style={{
                height:"100%", width:`${(cum[i]/max)*100}%`,
                background:i===2?C.blue:i===1?"#6b9ce8":"#9db8ef",
                borderRadius:4,
              }} />
            </div>
            <div style={{ width:150, fontSize:11, fontWeight:700, color:C.dark, textAlign:"right", whiteSpace:"nowrap" }}>
              ${Math.round(cum[i]/1e6)}M cumulative
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Assumptions({ items }) {
  return (
    <div style={{ background:"#f8fafc", border:"1px solid #e5e7eb", borderRadius:10, padding:"18px 22px", marginBottom:28 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.dark, marginBottom:12 }}>Model Assumptions</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:8 }}>
        {items.map(([k,v])=>(
          <div key={k} style={{ display:"flex", flexDirection:"column", gap:2 }}>
            <span style={{ fontSize:10, color:C.gray, textTransform:"uppercase", letterSpacing:"0.07em" }}>{k}</span>
            <span style={{ fontSize:11.5, color:C.dark, fontWeight:600 }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ForecastBody({ model }) {
  const years = Object.keys(model.years);
  const [year, setYear]       = useState(years[0]);

  const d = model.years[year];

  return (
    <div style={{ fontFamily:"system-ui,sans-serif", background:"#f9fafb", minHeight:"100vh" }}>
      <div style={{ background:C.dark, padding:"16px 32px", display:"flex",
        alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:20, fontWeight:700, color:"#fff" }}>Doorly</span>
          <span style={{ fontSize:11, color:C.gray, borderLeft:"1px solid #374151", paddingLeft:12 }}>
            Financial Forecast · Confidential
          </span>
        </div>
        <span style={{ fontSize:11, color:C.gray }}>NMLS #2764749</span>
      </div>

      <div style={{ maxWidth:1300, margin:"0 auto", padding:"32px 24px" }}>
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontSize:26, fontWeight:700, color:C.dark, margin:0 }}>
            {model.title}
          </h1>
        </div>

        <Assumptions items={model.assumptions} />
        <CumulativeBar model={model} />

        <div style={{ display:"flex", gap:8, marginBottom:18, alignItems:"center" }}>
          {years.map(y=>(
            <button key={y} onClick={()=>setYear(y)} style={{
              padding:"8px 22px", borderRadius:8, border:"none", cursor:"pointer",
              fontSize:13, fontWeight:700,
              background:year===y?C.blue:"#fff", color:year===y?"#fff":C.gray,
              boxShadow:year===y?"0 2px 8px rgba(58,106,217,0.25)":"0 1px 3px rgba(0,0,0,0.07)",
            }}>
              {y}
            </button>
          ))}
          <button onClick={() => exportForecastToExcel(model)} title="Download the full 2026–2028 model as a spreadsheet"
            style={{
              marginLeft:"auto", padding:"8px 16px", borderRadius:8, border:"none", cursor:"pointer",
              fontSize:13, fontWeight:700, background:C.dark, color:"#fff",
              display:"inline-flex", alignItems:"center", gap:6,
              boxShadow:"0 1px 3px rgba(0,0,0,0.12)",
            }}>
            ↓ Export to Excel
          </button>
        </div>

        <YearSummary d={d} avgLoanAmount={model.avgLoanAmount} />

        {model.sections.map(([id,lbl])=>(
          <div key={id} style={{ marginBottom:20 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.dark, marginBottom:10 }}>{lbl}</div>
            <DataTable d={d} defs={model.rows[id]} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ForecastPage() {
  // Access is the API's decision: staff, or an approved investor with a signed NDA.
  const [state, setState] = useState({ status: null, code: null, model: null });
  useEffect(() => {
    let alive = true;
    fetchGuide('/api/forecast/model').then((r) => { if (alive) setState({ status: r.status, code: r.code || null, model: r.data || null }); });
    return () => { alive = false; };
  }, []);
  if (state.status === 200 && state.model) return <ForecastBody model={state.model} />;
  const link = { color: C.blue, fontWeight: 600, textDecoration: "underline" };
  return (
    <div data-testid="forecast-locked" style={{ minHeight: "100vh", background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, boxSizing: "border-box", fontFamily: "system-ui,sans-serif" }}>
      <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 28, maxWidth: 480, width: "100%", boxSizing: "border-box" }}>
        <img src="/doorly_logo_black.png" alt="Doorly" style={{ height: 28, width: "auto", display: "block", marginBottom: 14 }} />
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8, color: C.dark }}>Doorly Financial Forecast</div>
        {state.status == null ? <div style={{ fontSize: 14, color: C.gray }}>Checking your access…</div>
          : state.code === "NDA_REQUIRED" ? <NdaPrompt />
            : state.status === 403 ? <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>The forecast is available to Doorly staff and to approved investors under NDA.</div>
              : (
                <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7 }}>
                  Confidential. Available to Doorly staff and approved investors under NDA. Sign in to view it:
                  <ul style={{ marginTop: 8, paddingLeft: 22 }}>
                    <li><a style={link} href="/admin/login">Doorly staff — DoorlyDesk sign-in</a></li>
                    <li><a style={link} href="/market/login">Investors — DoorlyMarket sign-in</a></li>
                  </ul>
                </div>
              )}
      </div>
    </div>
  );
}
